import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import DealershipsTable from "./DealershipsTable";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { AppDispatch } from "../../../redux/store";
import { fetchRegions } from "../../../redux/actions/countries.actions";
import { useSearchParams } from "react-router-dom";
import usePermissionCheck from "../../../hooks/usePermissionCheck";
import { FilterLineIcon, PlusIcon } from "../../../components/common/Icons/Icons";

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ['name', 'country', 'region'];

const DealershipsContainer: React.FC<{setDeleteOpen:any, setEditOpen:any,setAddOpen:any, setDealershipToModify:any}> = ({setDeleteOpen,setEditOpen, setAddOpen,setDealershipToModify}) => {
  const dispatch: AppDispatch = useDispatch();
  //Filters
  const {data:countries} = useSelector((state: RootState) => state.countries);
  const {data:regions} = useSelector((state: RootState) => state.regions);
  const [searchParams, setSearchParams] = useSearchParams();

  const {canAccess:canCreateDealership} = usePermissionCheck(['create_dealership']);

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');

  useEffect(() => {
    setName(searchParams.get('name')??'')
    setCountry(searchParams.get('country')??'')
    setRegion(searchParams.get('region')??'')
  }, [searchParams]);

  useEffect(() => {
    if (country!=='') {
      dispatch(fetchRegions(country));
    }
  }, [dispatch,country]);
 
  const handleSelect = (event: SelectChangeEvent, selectField: string) => {
    switch (selectField){
      case 'country':
        setCountry(event.target.value as string);
        setRegion('');
        break;
      case 'region':
        setRegion(event.target.value as string);
        break;
    }
  };

  const handleFilter = () => {
    searchParams.delete('page'); //reset page
    name==="" ? searchParams.delete('name') : searchParams.set('name', name);
    country==="" ? searchParams.delete('country') : searchParams.set('country', country);
    region==="" ? searchParams.delete('region') : searchParams.set('region', region);
    setSearchParams(searchParams);
  }

  const handleClear = () => {
    setName("");
    searchParams.delete('name');
    setCountry("");
    searchParams.delete('country');
    setRegion("");
    searchParams.delete('region');
    setSearchParams(searchParams);
  }

  const activeFilters = filterParams.filter((param)=>searchParams.has(param)).length;
  const regionLabel = country==='' || regions.length>0 ? `Region` : `No Regions Found.`;

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? 'active' : ''}`}
        onClick={()=>setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      {canCreateDealership &&
        <button type="button" className="btn btn-sm btn-primary lp-primary-action" onClick={()=>setAddOpen(true)}>
          <PlusIcon/> Add Dealership
        </button>}
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? '' : 'filters-hidden'}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              <TextField
                id="name-search"
                label="Name"
                type="search"
                fullWidth={true}
                value={name??""}
                onChange={(e)=>setName(e.target.value)}
                onKeyDown={(e)=>(e.code==="Enter" ? handleFilter() : null)}
                size="small"
                autoComplete="dealership-name"
              />

              <FormControl fullWidth size="small">
                <InputLabel id="country-filter-label">Country</InputLabel>
                <Select
                  labelId="country-filter-label"
                  label="Country"
                  value={country??""}
                  onChange={e => handleSelect(e,'country')}
                >
                  <MenuItem value={''}>All</MenuItem>
                  { countries?.length>0 &&
                    countries.map((country:any)=>
                      <MenuItem key={country?.id} value={country?.id}>{country?.label}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel id="region-filter-label">{regionLabel}</InputLabel>
                <Select
                  labelId="region-filter-label"
                  label={regionLabel}
                  value={region??""}
                  onChange={e => handleSelect(e,'region')}
                  disabled={regions.length===0}
                >
                  <MenuItem value={''}>All</MenuItem>
                  { regions?.length>0 && country!=='' &&
                    regions.map((region:any)=>
                      <MenuItem key={region?.id} value={region?.id}>{region?.label}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleFilter}>Apply Filters</button>
          </div>
        </aside>
      )}

      <DealershipsTable setDeleteOpen={setDeleteOpen} setEditOpen={setEditOpen} setDealershipToModify={setDealershipToModify} toolbar={toolbar}/>
    </div>
  );
};

export default DealershipsContainer;
